// @spec GHG-STR-055
'use client';

/**
 * Internal Verification Workflow - list page.
 * Shows all internal verification requests with filtering and creation.
 */

import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Plus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  useCreateVerificationRequest,
  useVerificationRequests,
  useVerificationSummary,
} from '@/features/ghg/api/use-verification';
import {
  CreateVerificationModal,
  InternalVerificationSummaryCard,
  VerificationRequestList,
} from '@/features/ghg/components';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/verification/internal/')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.internalVerification'),
  }),
  component: InternalVerificationPage,
});

function InternalVerificationPage() {
  const { t } = useTranslation('ghg');
  const [page, setPage] = React.useState(1);
  const [scopeFilter, setScopeFilter] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<string>('');
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const { periodId, period, isLoading: isPeriodLoading } = useGHGPagePeriod();

  const { verifications, total, isLoading, isFetching, refetch } = useVerificationRequests({
    scope: scopeFilter || undefined,
    status: statusFilter || undefined,
    page,
    pageSize: 20,
  });

  const { summary, isLoading: summaryLoading } = useVerificationSummary();
  const createMutation = useCreateVerificationRequest();

  const periodOptions = React.useMemo(() => {
    if (!period || !periodId) return [];
    return [
      {
        id: periodId,
        name: period.name,
      },
    ];
  }, [period, periodId]);

  const handleCreate = async (data: Parameters<typeof createMutation.mutateAsync>[0]) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('toast.verificationRequestCreated'));
      setIsCreateOpen(false);
      void refetch();
    } catch (e) {
      toast.error(t('toast.failedToCreateVerificationRequest'), {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-6xl space-y-8">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/ghg/verification">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-semibold text-foreground">{t('verification.internalVerification')}</h1>
              <p className="mt-1 text-muted-foreground">
                {t('verification.internalVerificationDesc')}
              </p>
            </div>
          </div>

            <Button
              onClick={() => {
                setIsCreateOpen(true);
              }}
              disabled={isPeriodLoading || !periodId || periodOptions.length === 0}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('verification.newVerification')}
            </Button>
          </header>

          {/* Summary Card */}
          <InternalVerificationSummaryCard summary={summary} isLoading={summaryLoading} />

          {/* Filters */}
          <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('verification.scopeFilter')}</span>
                <select
                  value={scopeFilter}
                  onChange={(e) => {
                    setScopeFilter(e.target.value);
                    setPage(1);
                  }}
                  className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                >
                  <option value="">{t('verification.filterAll')}</option>
                  <option value="scope_1">{t('verification.filterScope1')}</option>
                  <option value="scope_2">{t('verification.filterScope2')}</option>
                  <option value="scope_3">{t('verification.filterScope3')}</option>
                  <option value="all">{t('verification.filterAllScopes')}</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('verification.statusFilter')}</span>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                >
                  <option value="">{t('verification.filterAll')}</option>
                  <option value="draft">{t('verification.filterDraft')}</option>
                  <option value="under_review">{t('verification.filterUnderReview')}</option>
                  <option value="approved">{t('verification.filterApproved')}</option>
                  <option value="rejected">{t('verification.filterRejected')}</option>
                </select>
              </div>
            </div>
          </CardContent>
          </Card>

          {/* Verification List */}
          <VerificationRequestList
            verifications={verifications}
            total={total}
            page={page}
            isLoading={isLoading}
            isFetching={isFetching}
            onPageChange={setPage}
          />

          {/* Create Modal */}
          <CreateVerificationModal
            open={isCreateOpen}
            onOpenChange={setIsCreateOpen}
            onSubmit={handleCreate}
            isPending={createMutation.isPending}
            defaultReportingPeriodId={periodId}
            reportingPeriods={periodOptions}
            isLoadingPeriods={isPeriodLoading}
          />
      </div>
    </div>
  );
}
